
import { useFormik } from "formik";
import * as Yup from "yup";
import { InputText } from "primereact/inputtext";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { useState, useEffect, useRef, useMemo } from "react";
import { Toast } from "primereact/toast";
import { classNames } from "primereact/utils";
import { Calendar } from "primereact/calendar";
import {
  Vendor,
  RawMaterial,
} from "../../models/RawMaterials/RawMaterial-model";
import RawMaterialService, {
  getRawMaterialCategories,
  getRawMaterialMeasurementype,
} from "../../services/RawMaterials/RawMaterialService";
import posService from "../../services/pos/pos.service";

const PURCHASE_FIELD_ORDER = [
  "posID",
  "purchaseDate",
  "id",
  "vendorID",
  "invoiceId",
  "quantity",
  "basicAmount",
  "taxAmount",
] as const;

type RawMaterialPurchaseFormValues = {
  vendorID: number;
  purchaseDate: Date | null;
  invoiceId: string;
  id: number;
  posID: number;
  quantity: number | null;
  basicAmount: number;
  taxAmount: number;
  totalAmount: number;
};

type Props = {
  onSuccess?: () => void;
};

const RawMaterialPurchaseForm: React.FC<Props> = ({ onSuccess }) => {
  console.log("RawMaterialPurchaseForm FORM mounted");
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [rawMaterials, setRawMaterials] = useState<RawMaterial[]>([]);
  const [Pos, setPos] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<
    Array<{ categoryID: number; categoryName: string }>
  >([]);
  const [measurementType, setMeasurementType] = useState<string>("/unit");
  const [activeField, setActiveField] = useState<string | null>(null);
  const [mappedPosId, setMappedPosId] = useState<number | null>(null);
  const isMappedUser = useRef<boolean>(false);
  const toast = useRef<Toast>(null);

  const showToast = (
    severity: "success" | "error",
    summary: string,
    detail: string
  ) => {
    toast.current?.show({ severity, summary, detail, life: 3000 });
  };

  const formik = useFormik<RawMaterialPurchaseFormValues>({
    initialValues: {
      vendorID: 0,
      purchaseDate: null,
      invoiceId: "",
      id: 0,
      posID: 0,
      quantity: null,
      basicAmount: 0,
      taxAmount: 0,
      totalAmount: 0,
    },
    validationSchema: Yup.object({
      vendorID: Yup.number()
        .required("Vendor is required")
        .min(1, "Please select a vendor"),
      purchaseDate: Yup.date()
        .nullable()
        .required("Purchase date is required"),
      invoiceId: Yup.string()
        .required("Invoice Number is required")
        .matches(
          /^(?:[0-9]+|[a-zA-Z0-9]*[0-9][a-zA-Z0-9]*)$/,
          "Please enter a valid Invoice Number"
        ),
      id: Yup.number()
        .required("Raw Material is required")
        .min(1, "Please select a raw material"),
      posID: Yup.number()
        .required("Unit is required")
        .test("posID-required", "Please select a unit", function (value) {
          if (isMappedUser.current || Pos.length === 1) return true;
          return (value ?? 0) >= 1;
        }),
      basicAmount: Yup.number()
        .required("Base Price is required")
        .min(1, "Base Price must be greater than 0"),
      taxAmount: Yup.number()
        .required("Tax Amount is required")
        .min(0, "Tax Amount must be 0 or more"),
      quantity: Yup.number()
        .nullable()
        .required("Quantity is required")
        .moreThan(0, "Quantity must be greater than 0"),
    }),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        if (!values.purchaseDate) {
          showToast("error", "Validation Error", "Purchase date is required");
          return;
        }
        const d = values.purchaseDate;
        const purchaseDateStr = `${d.getFullYear()}-${String(
          d.getMonth() + 1
        ).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        const finalPosId =
          Pos.length === 1
            ? mappedPosId ?? Pos[0]?.posID ?? values.posID
            : values.posID;

        if (!finalPosId || finalPosId < 1) {
          showToast("error", "Validation Error", "Unit is required");
          return;
        }

        const payload = {
          rawMaterialId: values.id,
          vendorid: values.vendorID,
          purchaseDate: purchaseDateStr,
          invoiceId: values.invoiceId.toString(),
          quantity: values.quantity ?? 0,
          categoryId: selectedCategory?.[0]?.categoryID || 0,
          posId: finalPosId,
          basicAmount: values.basicAmount,
          taxAmount: values.taxAmount,
          totalAmount: values.basicAmount + values.taxAmount,
        };

        const response =
          await RawMaterialService.createRawMaterialPurchase(payload);
        if (response?.data?.isSuccess) {
          showToast("success", "Success", response.data.message);
          resetForm();
          setSelectedCategory([]);
          setMeasurementType("/unit");
          onSuccess?.();
        } else {
          showToast(
            "error",
            "Error",
            response?.data?.message || "Operation failed"
          );
        }
      } catch (error) {
        showToast(
          "error",
          "Error",
          "Failed to submit. Please try again later."
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [vendorList, rawMaterialList, mappedUnits, allUnits] =
          await Promise.all([
            RawMaterialService.getVendorList(),
            RawMaterialService.getRawMaterials(),
            RawMaterialService.GetManufacrturingunitlist(),
            posService.getAllPOS(),
          ]);

        setVendors(vendorList);
        setRawMaterials(rawMaterialList);

        const validatedMapped = Array.isArray(mappedUnits) ? mappedUnits : [];
        const validatedAll = Array.isArray(allUnits) ? allUnits : [];

        if (validatedMapped.length === 1) {
          isMappedUser.current = true;
          setPos(validatedMapped);
          setMappedPosId(validatedMapped[0].posID);
          formik.setFieldValue("posID", validatedMapped[0].posID, false);
          formik.setFieldTouched("posID", false, false);
        } else {
          isMappedUser.current = false;
          setPos(validatedAll);
          setMappedPosId(null);
        }
      } catch (error) {
        showToast("error", "Error", "Failed to load initial data");
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const lockedPosId =
      Pos.length === 1 ? mappedPosId ?? Pos[0]?.posID ?? null : mappedPosId;
    if (lockedPosId == null) return;
    formik.setFieldValue("posID", lockedPosId, false);
    formik.setFieldTouched("posID", false, false);
  }, [Pos, mappedPosId]);

  useEffect(() => {
    if (formik.submitCount === 0) return;

    const firstInvalidField = PURCHASE_FIELD_ORDER.find((field) =>
      Boolean(formik.errors[field])
    );
    if (!firstInvalidField) return;

    const target = document.getElementById(firstInvalidField);
    if (!target) return;

    target.scrollIntoView({ behavior: "smooth", block: "center" });
    const focusTarget = target.querySelector(
      "input, button, textarea, [tabindex]"
    ) as HTMLElement | null;
    (focusTarget ?? target).focus?.();
  }, [formik.errors, formik.submitCount]);

  const formatPOSLabel = (mu: any) => {
    const posCode = mu?.posCode?.trim();
    const posId = Number(mu?.posID ?? mu?.posId);
    const directAlias = (mu?.posAlias || mu?.POSAlias || mu?.PosAlias || mu?.pos_alias || "")?.trim();

    const posAlias =
      directAlias ||
      (posId === 1 || posCode === "POS-001"
        ? "NGBV"
        : posId === 2 || posCode === "POS-002"
        ? "MYS"
        : posId === 3 || posCode === "POS-003" || posCode === "KRN1"
        ? "KRN1"
        : posId === 4 || posCode === "POS-004" || posCode === "MGT"
        ? "MGT"
        : posId === 5 || posCode === "POS-005" || posCode === "KRN2"
        ? "KRN2"
        : posId === 6 || posCode === "POS-006" || posCode === "NGBV2"
        ? "NGBV2"
        : "");

    if (posCode && posAlias) return `${posCode} - ${posAlias}`;
    if (posCode) return posCode;
    return posAlias || `Unit-${mu?.posID}`;
  };

  const totalAmount = useMemo(() => {
    const base = Number(formik.values.basicAmount) || 0;
    const tax = Number(formik.values.taxAmount) || 0;
    return base + tax;
  }, [formik.values.basicAmount, formik.values.taxAmount]);

  const unitPricePreview = useMemo(() => {
    const qty = Number(formik.values.quantity) || 0;
    const base = Number(formik.values.basicAmount) || 0;
    if (!qty) return base;
    return base / qty;
  }, [formik.values.quantity, formik.values.basicAmount]);

  const showBasicAmountError =
    activeField !== "basicAmount" &&
    (formik.touched.basicAmount || formik.submitCount > 0) &&
    Number(formik.values.basicAmount) < 1;

  const handleRawMaterialChange = async (e: DropdownChangeEvent) => {
    const materialId = e.value;
    formik.setFieldValue("id", materialId);
    if (!materialId) {
      setSelectedCategory([]);
      setMeasurementType("/unit");
      return;
    }
    try {
      const category = await getRawMaterialCategories(materialId);
      setSelectedCategory(category || []);
      const measurementArray = await getRawMaterialMeasurementype(materialId);
      if (Array.isArray(measurementArray) && measurementArray.length > 0) {
        setMeasurementType(
          measurementArray[0].measurementTypeName ?? "/unit"
        );
      } else {
        setMeasurementType("/unit");
      }
    } catch (error) {
      console.error("Fetch error:", error);
    }
  };

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <form onSubmit={formik.handleSubmit} className="p-fluid">
        <div className="grid">
          {/* Left column: core details */}
          <div className="col-12 lg:col-8">
            <div className="card p-3">
              <div className="grid">
                <div className="field col-12 md:col-6">
                  <label className="text-sm font-semibold mb-2 block">
                    Unit <span className="text-red-400">*</span>
                  </label>
                  {Pos.length === 1 ? (
                    <InputText
                      value={formatPOSLabel(Pos[0]) || `${Pos[0].posCode || ""} - ${Pos[0].cityName || Pos[0].address2 || "Mapped"}`}
                      readOnly
                      className="w-full"
                    />
                  ) : (
                    <Dropdown
                      id="posID"
                      name="posID"
                      options={Pos.map((p) => ({
                        label: formatPOSLabel(p),
                        value: p.posID,
                      }))}
                      optionLabel="label"
                      optionValue="value"
                      value={formik.values.posID}
                      onChange={(e) =>
                        formik.setFieldValue("posID", e.value)
                      }
                      placeholder="Select Unit"
                      filter
                      className={classNames("w-full", {
                        "p-invalid":
                          (formik.touched.posID || formik.submitCount > 0) &&
                          formik.errors.posID,
                      })}
                    />
                  )}
                  {Pos.length !== 1 &&
                    (formik.touched.posID || formik.submitCount > 0) &&
                    formik.errors.posID && (
                      <small className="p-error">
                        {formik.errors.posID as string}
                      </small>
                    )}
                </div>

                <div className="field col-12 md:col-6">
                  <label className="text-sm font-semibold mb-2 block">
                    Purchase Date <span className="text-red-400">*</span>
                  </label>
                  <Calendar
                    id="purchaseDate"
                    name="purchaseDate"
                    value={formik.values.purchaseDate}
                    onChange={(e) =>
                      formik.setFieldValue("purchaseDate", e.value)
                    }
                    dateFormat="dd-mm-yy"
                    showIcon
                    className={classNames("w-full", {
                      "p-invalid":
                        (formik.touched.purchaseDate ||
                          formik.submitCount > 0) &&
                        formik.errors.purchaseDate,
                    })}
                  />
                  {(formik.touched.purchaseDate ||
                    formik.submitCount > 0) &&
                    formik.errors.purchaseDate && (
                      <small className="p-error">
                        {formik.errors.purchaseDate as string}
                      </small>
                    )}
                </div>

                <div className="field col-12 md:col-6">
                  <label className="text-sm font-semibold mb-2 block">
                    Raw Material <span className="text-red-400">*</span>
                  </label>
                  <Dropdown
                    id="id"
                    name="id"
                    options={rawMaterials}
                    optionLabel="name"
                    optionValue="id"
                    value={formik.values.id}
                    onChange={handleRawMaterialChange}
                    placeholder="Select Raw Material"
                    filter
                    className={classNames("w-full", {
                      "p-invalid":
                        (formik.touched.id || formik.submitCount > 0) &&
                        formik.errors.id,
                    })}
                  />
                  {(formik.touched.id || formik.submitCount > 0) &&
                    formik.errors.id && (
                      <small className="p-error">
                        {formik.errors.id as string}
                      </small>
                    )}
                </div>

                <div className="field col-12 md:col-6">
                  <label className="text-sm font-semibold mb-2 block">
                    Vendor <span className="text-red-400">*</span>
                  </label>
                  <Dropdown
                    id="vendorID"
                    name="vendorID"
                    options={vendors}
                    optionLabel="firmInfo"
                    optionValue="vendorID"
                    value={formik.values.vendorID}
                    onChange={(e) =>
                      formik.setFieldValue("vendorID", e.value)
                    }
                    placeholder="Select Vendor"
                    filter
                    className={classNames("w-full", {
                      "p-invalid":
                        (formik.touched.vendorID ||
                          formik.submitCount > 0) &&
                        formik.errors.vendorID,
                    })}
                  />
                  {(formik.touched.vendorID || formik.submitCount > 0) &&
                    formik.errors.vendorID && (
                      <small className="p-error">
                        {formik.errors.vendorID as string}
                      </small>
                    )}
                </div>

                <div className="field col-12 md:col-6">
                  <label className="text-sm font-semibold mb-2 block">
                    Invoice Number
                  </label>
                  <InputText
                    id="invoiceId"
                    name="invoiceId"
                    value={formik.values.invoiceId}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="E.g. INV-001"
                    className={classNames("w-full", {
                      "p-invalid":
                        (formik.touched.invoiceId ||
                          formik.submitCount > 0) &&
                        formik.errors.invoiceId,
                    })}
                  />
                  {(formik.touched.invoiceId || formik.submitCount > 0) &&
                    formik.errors.invoiceId && (
                      <small className="p-error">
                        {formik.errors.invoiceId as string}
                      </small>
                    )}
                </div>

                <div className="field col-12 md:col-6">
                  <label className="text-sm font-semibold mb-2 block">
                    Invoiced Quantity
                  </label>
                  <div className="p-inputgroup">
                    <InputNumber
                      id="quantity"
                      name="quantity"
                      value={formik.values.quantity}
                      onValueChange={(e) =>
                        formik.setFieldValue("quantity", e.value)
                      }
                      mode="decimal"
                      step={0.01}
                      minFractionDigits={0}
                      maxFractionDigits={3}
                      min={formik.values.quantity === null ? undefined : 0}
                      placeholder="Invoiced Qty"
                      inputClassName="w-full"
                    />
                    <span className="p-inputgroup-addon">
                      {measurementType}
                    </span>
                  </div>
                  {(formik.touched.quantity || formik.submitCount > 0) &&
                    formik.errors.quantity && (
                      <small className="p-error">
                        {formik.errors.quantity as string}
                      </small>
                    )}
                </div>
              </div>
            </div>
          </div>

          {/* Right column: pricing */}
          <div className="col-12 lg:col-4">
            <div className="card p-3">
              <div className="field mb-3">
                <label className="text-sm font-semibold mb-2 block">
                  Base Price (₹) *
                </label>
                <InputNumber
                  id="basicAmount"
                  name="basicAmount"
                  value={formik.values.basicAmount}
                  onValueChange={(e) =>
                    formik.setFieldValue("basicAmount", e.value ?? 0)
                  }
                  onFocus={() => setActiveField("basicAmount")}
                  onBlur={(e) => {
                    formik.handleBlur(e);
                    setActiveField(null);
                  }}
                  mode="currency"
                  currency="INR"
                  locale="en-IN"
                  className={classNames("w-full", {
                    "p-invalid": showBasicAmountError,
                  })}
                />
                {showBasicAmountError && (
                  <small className="p-error">
                    Base Price must be greater than 0
                  </small>
                )}
              </div>

              <div className="field mb-3">
                <label className="text-sm font-semibold mb-2 block">
                  Unit Price (₹)
                </label>
                <InputNumber
                  value={unitPricePreview}
                  mode="currency"
                  currency="INR"
                  locale="en-IN"
                  readOnly
                  disabled
                  className="w-full"
                />
              </div>

              <div className="field mb-3">
                <label className="text-sm font-semibold mb-2 block">
                  Tax Amount (₹) *
                </label>
                <InputNumber
                  id="taxAmount"
                  name="taxAmount"
                  value={formik.values.taxAmount}
                  onValueChange={(e) =>
                    formik.setFieldValue("taxAmount", e.value ?? 0)
                  }
                  mode="currency"
                  currency="INR"
                  locale="en-IN"
                  className={classNames("w-full", {
                    "p-invalid":
                      (formik.touched.taxAmount ||
                        formik.submitCount > 0) &&
                      formik.errors.taxAmount,
                  })}
                />
                {(formik.touched.taxAmount || formik.submitCount > 0) &&
                  formik.errors.taxAmount && (
                    <small className="p-error">
                      {formik.errors.taxAmount as string}
                    </small>
                  )}
              </div>

              <div className="field mb-3">
                <label className="text-sm font-semibold mb-2 block">
                  Total Amount (₹)
                </label>
                <InputNumber
                  value={totalAmount}
                  mode="currency"
                  currency="INR"
                  locale="en-IN"
                  readOnly
                  disabled
                  className="w-full"
                />
              </div>

              <div className="field">
                <Button
                  type="submit"
                  label={formik.isSubmitting ? "Processing..." : "Submit"}
                  icon={
                    formik.isSubmitting
                      ? "pi pi-spinner pi-spin"
                      : "pi pi-check"
                  }
                  disabled={formik.isSubmitting}
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default RawMaterialPurchaseForm;
